import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Select, Stack, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { fetchUsers } from "../../../fetchers/user";
import { toAssigneeOptions } from "../../cmms/cmmsAssigneeOptions";
import { fetchAssetHierarchyTree } from "../../../fetchers/cmmsAssetHierarchy";
import {
  createWorkOrderTask,
  updateWorkOrderTask,
  changeWorkOrderTaskStatus,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { flattenAssetNodes } from "../../cmms/assetHierarchyUtils";
import { WorkOrderTaskStatus, getWorkOrderTaskStatusOptions } from "../workOrderEnums";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getMenuSx,
  getOutlinedButtonSx,
  getSelectSx,
} from "../../cmms/assetHierarchyStyles";

const emptyForm = {
  title: "",
  description: "",
  assignedResourceId: null,
  linkedAssetNodeId: null,
  sortOrder: 0,
  status: WorkOrderTaskStatus.Open,
};

export default function WorkOrderTaskFormDialog({ open, onClose, workOrderId, task, companyKey, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const isEdit = Boolean(task);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  const assetOptions = useMemo(
    () => flattenAssetNodes(treeQuery.data).map((node) => ({ id: node.id, title: node.name })),
    [treeQuery.data],
  );

  const userOptions = useMemo(
    () => toAssigneeOptions(usersQuery.data, form.assignedResourceId),
    [usersQuery.data, form.assignedResourceId],
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit && task) {
      setForm({
        title: task.title ?? "",
        description: task.description ?? "",
        assignedResourceId: task.assignedResourceId ?? null,
        linkedAssetNodeId: task.linkedAssetNodeId ?? null,
        sortOrder: task.sortOrder ?? 0,
        status: task.status ?? WorkOrderTaskStatus.Open,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, isEdit, task]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleTextChange = (event) => {
    handleField(event.target.name || event.target.id, event.target.value);
  };

  const validate = () => {
    const next = {};
    if (!form.title?.trim()) next.title = intl.formatMessage(messages.relatedDocumentsTitleRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        companyKey,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        assignedResourceId: form.assignedResourceId || null,
        linkedAssetNodeId: form.linkedAssetNodeId || null,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (isEdit) {
        await updateWorkOrderTask(workOrderId, task.id, payload);
        if (form.status !== task.status) {
          await changeWorkOrderTaskStatus(workOrderId, task.id, { companyKey, newStatus: form.status });
        }
        pushNotification(intl.formatMessage(messages.toastTaskUpdated));
      } else {
        await createWorkOrderTask(workOrderId, payload);
        pushNotification(intl.formatMessage(messages.toastTaskCreated));
      }
      onSuccess?.();
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tasksSaveFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  const findOption = (options, id) => options.find((option) => option.id === id) ?? null;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(isEdit ? messages.tasksEditTitle : messages.tasksAdd)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <AxionControls.AxionTextBox
          name="title"
          label={intl.formatMessage(messages.fieldTitleRequired)}
          value={form.title}
          onChange={handleTextChange}
          error={errors.title}
        />
        <AxionControls.AxionMultiLineText
          name="description"
          label={intl.formatMessage(messages.fieldDescription)}
          value={form.description}
          rows={3}
          onChange={handleTextChange}
        />
        {isEdit &&
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldStatus} /></Typography>
            <Select
              fullWidth
              size="small"
              value={form.status}
              onChange={(event) => handleField("status", event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              {getWorkOrderTaskStatusOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
        }
        <AxionControls.AxionAutocomplete
          name="assignedResourceId"
          label={intl.formatMessage(messages.fieldAssignedResource)}
          options={userOptions}
          value={findOption(userOptions, form.assignedResourceId)}
          onChange={(_event, option) => handleField("assignedResourceId", option?.id ?? null)}
        />
        <AxionControls.AxionAutocomplete
          name="linkedAssetNodeId"
          label={intl.formatMessage(messages.fieldLinkedAsset)}
          options={assetOptions}
          value={findOption(assetOptions, form.linkedAssetNodeId)}
          onChange={(_event, option) => handleField("linkedAssetNodeId", option?.id ?? null)}
        />
        <AxionControls.AxionTextBox
          name="sortOrder"
          label={intl.formatMessage(messages.fieldSortOrder)}
          value={form.sortOrder}
          onChange={handleTextChange}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
